import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { App } from '@df/core/app';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { SocialService } from '@df/ui/social/social.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';

export interface ISocialLinks extends NICmsContentComponent.IData {
    title: string;
    items: {
        platform?: TPlatform;
        link?: string;
    }[];
    mode: 'link' | 'share';
}

type TPlatform = 'facebook' | 'twitter' | 'instagram' | 'pinterest' | 'youtube';

@ICmsComponent<PageComponentSocialLinksComponent>({
    id: 'social-links',
    name: 'Social links',
    tags: ['Inspiration page', 'Blog page'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'mode',
            type: NICmsContentField.EFieldType.choice,
            title: 'Mode',
            default: 'share',
            options: [
                { key: 'share', title: 'Share buttons' },
                { key: 'link', title: 'Links to social profiles' }
            ]
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'items',
            title: 'Items',
            itemName: 'Item',
            itemNameField: 'platform',
            defaultSize: 3,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'platform',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Platform',
                    required: true,
                    options: [
                        { key: 'facebook', title: 'Facebook' },
                        { key: 'twitter', title: 'Twitter' },
                        { key: 'instagram', title: 'Instagram' },
                        { key: 'pinterest', title: 'Pinterest' },
                        { key: 'youtube', title: 'Youtube' }
                    ]
                },
                <NICmsContentField.IField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    canShow: (data: ISocialLinks) => data.mode === 'link'
                }
            ]
        }
    ]
})
@Component({
    selector: 'page-social-links',
    templateUrl: './social-links.html',
    standalone: true,
    imports: [CommonModule, HrefComponent, ICmsClientModule, SizeClassDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentSocialLinksComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: ISocialLinks;

    protected socialService = inject(SocialService);
    protected app = inject(App);

    share(platform: TPlatform) {
        if (this.component.mode !== 'share') {
            return;
        }
        const model = this.app.meta.model;
        switch (platform) {
            case 'facebook':
                return this.socialService.fbShare(model);
            case 'twitter':
                return this.socialService.twShare(model);
            case 'pinterest':
                return this.socialService.pinShare(model);
        }
    }
}
